import React, { useEffect, useRef, useState } from 'react';
import {
	Dialog,
	DialogActions,
	Box,
	useMediaQuery
} from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowLeft } from '@mui/icons-material';
import { useLottie } from 'lottie-react';
import { nanoid } from 'nanoid';
import { PDFDocument } from 'pdf-lib';
import PropTypes from 'prop-types';
import ApproveRejectDialog from '../../../Common/Dashboard/approve-reject-dialog.js';
import { ReactComponent as CrossIcon } from '../../../../Assets/Images/close-circle-white.svg';
import PrimaryButton from '../../../Common/Button/primary-button.js';
import animationData from '../../../../Assets/Images/Loader.json';
import { putApprovalRequestEsignAction, putEditCancelEsignApprovalRequest, putEditCancelEsignRejectRequest } from '../../../../Services/api-config.js';
import SuccessRejectModal from '../../../Common/success-reject-modal.js';
import FeedbackDialog from '../../../Common/Dashboard/feedback-dialog.js';
import { downloadPDF } from '../../../../utils/dowload-pdf.js';
import Loading from '../../../Common/LoaderComponent/loader.js';
import customToast from '../../../Common/custom-toast.js';
import { formatFeedbackInput, getErrorMessage } from '../../../../utils/util.js';
import DocumentViewer from '../../../Common/Dashboard/document-viewer.js';
import './organization-dashboard.css';
import './pdf-details.css';
import RenderDetails from './render-details.js';
import PDFDetailsMobile from './pdfdetails-mobile.js';

function StatusIndicator({ color, text }) {
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: '10px',
				marginBottom: '10px'
			}}
		>
			<div
				style={{
					border: `2px  solid  ${color}`,
					height: '25px',
					width: '25px',
					borderRadius: '50%',
					boxShadow: `0 0 5px ${color}`
					// BackgroundColor: bgcolor
				}}
			></div>
			<p
				style={{
					color: 'white',
					fontSize: '12px',
					fontWeight: 'bold',
					padding: '0',
					margin: '0'
				}}
			>
				{text}
			</p>
		</div>
	);
}

StatusIndicator.propTypes = {
	color: PropTypes.any,
	text: PropTypes.any
};

const PDFDetails = ({
	open,
	onClose,
	file,
	onFetchApi,
	view,
	hideAction,
	showSign,
	showInfo = false,
	rejectedTab = false
}) => {
	const { showToast } = customToast();
	const [loader, setLoader] = useState(true);
	const pdfContainerRef = useRef();
	const [aprroveReject, setAprroveReject] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [sideContent, setSideContent] = useState(false);
	const [numPages, setNumPages] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [type, setType] = useState('');
	const [saveSuccessReject, setSaveSuccessReject] = useState(false);
	const [openFeedback, setOpenFeedback] = useState(false);
	const [feedback, setFeedback] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [arrowIcon, setArrowIcon] = useState('right');
	const [selectedTab, setSelectedTab] = React.useState(0);
	const isMobile = useMediaQuery('(max-width: 600px)');
	const initialDisplayCount = 2;
	const [signCoordinates, setSignCoordinates] = useState([]);
	const [isActionButtonDisabled, setIsActionButtonDisabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');

	const options = {
		animationData,
		loop: true,
		autoplay: true
	};
	const { View } = useLottie(options);

	useEffect(() => {
		setSideContent(true);
		setArrowIcon('left');
		setAprroveReject(false);
		setType('');
		setFeedback('');
		setErrorMessage('');
		setShowAll(false);
		setSelectedTab(0);
		setIsActionButtonDisabled(false);
		setIsLoading(false);
	}, [open, view]);

	const displayedRecipients = showAll
		? file?.recipientData
		: file?.recipientData?.slice(0, initialDisplayCount);

	const onDocumentLoadSuccess = ({ numPages }) => {
		setNumPages(numPages);
		setCurrentPage(1);
	};

	const toggleShowAll = () => {
		setShowAll(!showAll);
	};

	const handleApprove = () => {
		setAprroveReject(true);
		setType('Approval');
	};

	const handleReject = () => {
		setAprroveReject(true);
		setType('Reject');
	};

	const handleAction = () => {
		if (type === 'Approval') {
			handleApproveOrder();
		} else if (type === 'Reject') {
			setOpenFeedback(true);
		}
	};

	const handleApproveOrder = async () => {
		try {
			setIsActionButtonDisabled(true);
			setIsLoading(true);

			let response;

			if (['Awaiting Edit Approval', 'Awaiting Cancel Approval'].includes(file?.status)) {
				response = await putEditCancelEsignApprovalRequest(file?.documentId);
			} else {
				response = await putApprovalRequestEsignAction(file?.documentId, {
					action: 'approve'
				});
			}

			if (response?.status === 200) {
				setIsActionButtonDisabled(false);
				setIsLoading(false);
				setSaveSuccessReject(true);
				setType('Approval');
			} else {
				setIsActionButtonDisabled(false);
				setIsLoading(false);
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			setIsActionButtonDisabled(false);
			setIsLoading(false);
			showToast('error', getErrorMessage(error));
		}
	};

	useEffect(() => {
		if (file?.documentId) {
			if (view) {
				handleLoadPdf(file);
			} else {
				setSignCoordinates([]);
			}
		}

		return () => {
			setSignCoordinates([]);
		};
	}, [file]);

	const handleLoadPdf = async (fileData) => {
		const pdfId = nanoid(5);

		const loadDocument = await PDFDocument.load(fileData.documentPdf);
		if (!loadDocument) {
			showToast('error', 'There was some error');
			return;
		}

		const getPdfPages = loadDocument?.getPages();

		const newCoordinats = [];

		if (fileData?.recipientData) {
			const arr = fileData?.recipientData;
			for (const itemData of arr) {
				if (itemData.coordinates) {
					for (const [index, coord] of itemData.coordinates.entries()) {
						const pageData = getPdfPages[index];

						const getPdfPageWidth = Math.floor(pageData.getWidth());
						const getPdfPageHeight = Math.floor(pageData.getHeight());

						if (fileData.templateId) {
							coord.y -= 10;
						}

						const coordiantes = {
							name: itemData.recipientId,
							value: itemData.recipientId,
							uuid: itemData.recipientId,
							inval: [
								itemData.recipientFirstName,
								itemData?.recipientMiddleName,
								itemData?.recipientLastName
							]
								.filter(Boolean)
								.join(' '),
							pdfId,
							signId: '',
							pdfdataUrl: '',
							pdfdocId: fileData?.documentId || '',
							coord: {
								x: Number(coord.x),
								y: Number(coord.y),
								pageNo: Number(coord.pageNo) || 1,
								pageWidth: getPdfPageWidth,
								pageHeight: getPdfPageHeight
							}
						};
						newCoordinats.push(coordiantes);
					}
				}
			}
		}

		setSignCoordinates(newCoordinats);
	};

	const handleRejectOrder = async () => {
		try {
			const tempFeedback = feedback?.trim();
			if (!tempFeedback) {
				showToast('error', 'Please add feedback');
				return;
			}

			setIsLoading(true);
			const feedbackStatus = { action: 'reject', feedback: tempFeedback };
			let response;

			if (['Awaiting Edit Approval', 'Awaiting Cancel Approval'].includes(file?.status)) {
				response = await putEditCancelEsignRejectRequest(
					file?.documentId,
					{feedback: tempFeedback}
				);
			} else {
				response = await putApprovalRequestEsignAction(
					file?.documentId,
					feedbackStatus
				);
			}

			if (response?.status === 200) {
				setMessage(response.message);
				setSaveSuccessReject(true);
				setOpenFeedback(false);
				setType('Reject');
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const toggleDialogWidth = () => {
		setSideContent((prevSideContent) => {
			setArrowIcon(prevSideContent ? 'right' : 'left');
			return !prevSideContent;
		});
	};

	const handleScroll = () => {
		if (pdfContainerRef.current) {
			const scrollTop = pdfContainerRef.current.scrollTop;
			const pageHeight = pdfContainerRef.current.scrollHeight / numPages;
			const newPage = Math.floor(scrollTop / pageHeight) + 1;
			setCurrentPage(newPage);
		}
	};

	const handleChange = (event, newValue) => {
		setSelectedTab(newValue);
	};

	return (
		<>
			{isLoading && (
				<Loading
					open={isLoading}
					onClose={() => {
						setLoader(true);
						setIsLoading(false);
					}}
				/>
			)}
			{isMobile && !aprroveReject && !saveSuccessReject && (
				<PDFDetailsMobile
					open={open}
					onClose={onClose}
					selectedTab={selectedTab}
					handleChange={handleChange}
					pdfContainerRef={pdfContainerRef}
					handleScroll={handleScroll}
					file={file}
					onDocumentLoadSuccess={onDocumentLoadSuccess}
					View={View}
					numPages={numPages}
					currentPage={currentPage}
					signCoordinates={signCoordinates}
					displayedRecipients={displayedRecipients}
					showAll={showAll}
					initialDisplayCount={initialDisplayCount}
					toggleShowAll={toggleShowAll}
					view={view}
					handleApprove={handleApprove}
					handleReject={handleReject}
					hideAction={hideAction}
					showSign={showSign}
					showInfo={showInfo}
					rejectedTab={rejectedTab}
				/>
			)}
			{!isMobile && !aprroveReject && !saveSuccessReject && (
				<Dialog
					open={open}
					maxWidth={false}
					slotProps={{
						paper: {
							style: {
								borderRadius: '10px',
								background: 'black'
							}
						}
					}}
				>
					<div
						style={{
							color: 'white',
							display: 'flex',
							flexDirection: 'row',
							padding: '20px'
						}}
					>
						<div style={{ width: '100%' }}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
								<div style={{ display: 'flex', gap: '10px' }}>
									{view && (
										<Box
											onClick={() => {
												setLoader(true);
												setCurrentPage(1);
												onClose();
											}}
										>
											<CrossIcon
												style={{
													stroke: '#fff',
													cursor: 'pointer',
													width: '24px',
													height: '24px',
													strokeWidth: '2px'
												}}
											/>
										</Box>
									)}
									<div>{file?.documentName}</div>
									{!view && numPages > 0 && (
										<div
											style={{ flex: 1, position: 'absolute', right: '270px' }}
										>
											<p>
												{currentPage}/{numPages}
											</p>
										</div>
									)}
								</div>

								{view && (
									<div>
										{arrowIcon === 'right' ? (
											<KeyboardArrowRight
												style={{
													cursor: 'pointer',
													transition: 'width 1s ease-out'
												}}
												onClick={toggleDialogWidth}
											/>
										) : (
											<KeyboardArrowLeft
												style={{
													cursor: 'pointer',
													transition: 'width 1s ease-out'
												}}
												onClick={toggleDialogWidth}
											/>
										)}
									</div>
								)}
								{!view && (
									<Box
										onClick={() => {
											setLoader(true);
											onClose();
										}}
									>
										<CrossIcon
											style={{
												stroke: '#fff',
												cursor: 'pointer',
												width: '24px',
												height: '24px',
												strokeWidth: '2px'
											}}
										/>
									</Box>
								)}
							</div>
							<div style={{ display: 'flex' }}>
								<div
									className='pdfListView'
									style={{ maxWidth: sideContent ? '71%' : '100%' }}
								>
									<div className='pdfpreview-container'>
										<DocumentViewer
											data={{stampPaperPdfPath: file?.documentPdf, documentName: ''}}
											numPages={numPages}
											currentPage={currentPage}
											isMobile={isMobile}
											documentLoading={loader}
											setDocumentLoading={setLoader}
											View={<p>Loading...</p>}
											isEsignFlow
											showOptions={false}
											setNumPages={setNumPages}
											setPageNumber={setCurrentPage}
											showSign={showSign}
											file={file}
										/>
									</div>
									{view && (
										<DialogActions style={{ padding: '10px 0 0' }}>
											{numPages > 0 && (
												<div style={{ flex: 1 }}>
													<p>
														{currentPage}/{numPages}
													</p>
												</div>
											)}
											{!hideAction && (
												<>
													<PrimaryButton
														value={'Reject'}
														onClick={handleReject}
														textcolor='#E96666'
														padding='10px 16px'
														width='97px'
														height='40px'
														backgroundColor='transparent'
														borderRadius='8px'
														border='2px solid #E96666'
													/>
													<PrimaryButton
														value='Approve & Send'
														onClick={handleApprove}
														backgroundColor='#B7D423'
														padding='0'
														textcolor='#000000'
														width='130px'
														height='40px'
														borderRadius='8px'
													/>
												</>
											)}
										</DialogActions>
									)}
								</div>
								<div
									className='pdfDetailsList'
									style={{
										width: sideContent ? '250px' : '0',
										position: 'relative'
									}}
								>
									<div
										style={{ maxHeight: '70vh' }}
										className='RightSideDetails'
									>
										<RenderDetails
											displayedRecipients={displayedRecipients}
											file={file}
											downloadPDF={downloadPDF}
											showAll={showAll}
											toggleShowAll={toggleShowAll}
											initialDisplayCount={initialDisplayCount}
											totalRecipients={file?.recipientData?.length}
											showCompare={file?.status === 'Awaiting Edit Approval'}
											documentId={file?.documentId}
										/>
									</div>
									{showInfo ? (
										<div
											style={{
												position: 'absolute',
												bottom: '20px',
												left: '20px',
												backgroundColor: 'black'
											}}
										>
											<div
												style={{
													display: 'flex',
													flexWrap: 'wrap',
													gap: '10px'
												}}
											>
												<StatusIndicator color='green' text='Signed' />
												<StatusIndicator color='#FFDA15' text='Sign Pending' />
												<StatusIndicator
													color='#f44336'
													text={rejectedTab ? 'Rejected' : 'Link Expired'}
												/>
											</div>
										</div>
									) : null}
								</div>
							</div>
						</div>
					</div>
				</Dialog>
			)}
			{!saveSuccessReject && !openFeedback && (
				<ApproveRejectDialog
					open={aprroveReject}
					onClose={() => {
						setLoader(true);
						onClose();
					}}
					type={type}
					DialogTitles={
						type === 'Approval' ? 'Approve Workflow ' : 'Reject Workflow'
					}
					DialogContents={
						type === 'Approval'
							? 'Are you sure you want to approve the workflow?'
							: 'Are you sure you want to reject the workflow?'
					}
					checkBalance={false}
					handleCancelButton={'No, Cancel'}
					handleActionButton={
						type === 'Approval' ? 'Yes, Approve & Send' : 'Yes, Reject'
					}
					handleActionFunction={handleAction}
					width={type === 'Approval' ? '164px' : '100px'}
					isActionButtonDisabled={isActionButtonDisabled}
				/>
			)}
			{openFeedback && (
				<FeedbackDialog
					open={openFeedback}
					onClose={() => {
						setOpenFeedback(false);
						setLoader(true);
						onClose();
					}}
					handleAction={handleRejectOrder}
					title=''
					buttonText='Send Feedback'
					feedback={feedback}
					onChangeFeedback={(e) => {
						const rawInput = e.target.value;
						const value = formatFeedbackInput(rawInput);
						setFeedback(value);
					}}
					errorMessage={errorMessage}
					loading={isLoading}
				/>
			)}
			<SuccessRejectModal
				open={saveSuccessReject}
				onClose={() => {
					setSaveSuccessReject(false);
					setLoader(true);
					onClose();
				}}
				title={type === 'Approval' ? 'Successful!' : 'Rejected'}
				message={
					type === 'Approval'
						? 'You have successfully approved the request'
						: message
				}
				onActionClick={onFetchApi}
				actionLabel='Close'
				modalContentWidth='276px'
				type={type}
			/>
		</>
	);
};

export default PDFDetails;

PDFDetails.propTypes = {
	open: PropTypes.any,
	onClose: PropTypes.any,
	file: PropTypes.any,
	onFetchApi: PropTypes.any,
	view: PropTypes.any,
	hideAction: PropTypes.any,
	showSign: PropTypes.any,
	showInfo: PropTypes.any,
	rejectedTab: PropTypes.any
};
